import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TrendingUp, TrendingDown, DollarSign, AlertCircle, Wallet } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LineChart, Line,
} from 'recharts';
import { toast } from 'sonner';

const fmt = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CashFlowTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(String(currentYear));

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/dashboard/cash-flow', { params: { year } });
      setData(res.data);
    } catch (e) {
      toast.error('Error al cargar el flujo de caja');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 4; y--) yearOptions.push(String(y));

  if (loading) {
    return <div className="p-8 text-center text-slate-400" data-testid="cashflow-loading">Cargando flujo de caja...</div>;
  }
  if (!data) return null;

  const s = data.summary || {};

  return (
    <div className="p-4 space-y-6" data-testid="cashflow-tab">
      {/* Header + year selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Wallet className="w-6 h-6 text-blue-600" /> Flujo de Caja
          </h2>
          <p className="text-sm text-slate-500">Ingresos vs Egresos mensuales del año {data.year}</p>
        </div>
        <div className="w-40">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger data-testid="cashflow-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {yearOptions.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-green-100 bg-green-50/50" data-testid="summary-income">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Ingresos</p>
                <p className="text-xl font-bold text-green-700">{fmt(s.total_income)}</p>
              </div>
              <TrendingUp className="w-7 h-7 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-100 bg-red-50/50" data-testid="summary-expenses">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Egresos (Nóminas)</p>
                <p className="text-xl font-bold text-red-700">{fmt(s.total_expenses)}</p>
              </div>
              <TrendingDown className="w-7 h-7 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card className={`${(s.net_balance || 0) >= 0 ? 'border-blue-100 bg-blue-50/50' : 'border-orange-100 bg-orange-50/50'}`} data-testid="summary-net">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Balance Neto</p>
                <p className={`text-xl font-bold ${(s.net_balance || 0) >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>{fmt(s.net_balance)}</p>
              </div>
              <DollarSign className={`w-7 h-7 ${(s.net_balance || 0) >= 0 ? 'text-blue-500' : 'text-orange-500'}`} />
            </div>
          </CardContent>
        </Card>
        <Card className="border-yellow-100 bg-yellow-50/50" data-testid="summary-pending">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Por Cobrar</p>
                <p className="text-xl font-bold text-yellow-700">{fmt(s.pending_invoices_total)}</p>
                <p className="text-[10px] text-slate-500">{s.pending_invoices_count || 0} factura(s)</p>
              </div>
              <AlertCircle className="w-7 h-7 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bar chart Income vs Expenses */}
      <Card>
        <CardHeader><CardTitle className="text-base">Ingresos vs Egresos por Mes</CardTitle></CardHeader>
        <CardContent>
          <div style={{ width: '100%', height: 320 }} data-testid="cashflow-bar-chart">
            <ResponsiveContainer>
              <BarChart data={data.months} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month_name" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Legend />
                <Bar dataKey="income" name="Ingresos" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" name="Egresos" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Line chart - Net balance trend */}
      <Card>
        <CardHeader><CardTitle className="text-base">Balance Neto Mensual</CardTitle></CardHeader>
        <CardContent>
          <div style={{ width: '100%', height: 240 }} data-testid="cashflow-line-chart">
            <ResponsiveContainer>
              <LineChart data={data.months} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month_name" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Line type="monotone" dataKey="net" name="Balance Neto" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Monthly breakdown table */}
      <Card>
        <CardHeader><CardTitle className="text-base">Detalle Mensual</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cashflow-table">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <th className="py-2 px-2">Mes</th>
                  <th className="py-2 px-2 text-right">Ingresos</th>
                  <th className="py-2 px-2 text-right">Egresos</th>
                  <th className="py-2 px-2 text-right">Neto</th>
                </tr>
              </thead>
              <tbody>
                {data.months.map(m => (
                  <tr key={m.month} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-2 font-medium text-slate-700">{m.month_name}</td>
                    <td className="py-2 px-2 text-right text-green-600">{fmt(m.income)}</td>
                    <td className="py-2 px-2 text-right text-red-600">{fmt(m.expenses)}</td>
                    <td className={`py-2 px-2 text-right font-semibold ${m.net >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>{fmt(m.net)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-bold bg-slate-50">
                  <td className="py-2 px-2">Total</td>
                  <td className="py-2 px-2 text-right text-green-700">{fmt(s.total_income)}</td>
                  <td className="py-2 px-2 text-right text-red-700">{fmt(s.total_expenses)}</td>
                  <td className={`py-2 px-2 text-right ${(s.net_balance || 0) >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>{fmt(s.net_balance)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
